import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaAdsChartDocs } from './mopa-ads.chart-documentation';
import { AdsActivityDataLoaderFactory } from './data-loaders/ads-activity';
import { AdsARPUDataLoaderFactory } from './data-loaders/ads-arpu';
import { AdsComparisonDataLoaderFactory } from './data-loaders/ads-comparison';
import { AdsDauDeuDataLoaderFactory } from './data-loaders/ads-dau-deu';
import { AdsRevenuePlatformDataLoaderFactory } from './data-loaders/ads-revenue-platform';
import { BIVizDashboardAdEvents } from './sql-maps/biviz-ad-events-datasource-sql-map';
import { BiVizReportAdsDatasourceSqlMap } from './sql-maps/biviz-report-ads-datasource-sql-map';

@Component({
    selector: 'app-mopa-ads',
    templateUrl: './mopa-ads.component.html',
    styleUrls: ['./mopa-ads.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaAdsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Ads';
    override DASHBOARD_ID = 'mopa-ads';
    values = Object.values;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        // ad_platform
        'mopa.ads.filter.ad_platform': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.ad_platform',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'AD_PLATFORM',
            datasourceSqlMap: [BIVizDashboardAdEvents.sqlMap, BiVizReportAdsDatasourceSqlMap.sqlMap],
        }).create(),
        // ad_name
        'mopa.ads.filter.ad_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.ad_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'AD_NAME',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
        }).create(),
        // ad_consent
        'mopa.ads.filter.daily_ad_consent': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.daily_ad_consent',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DAILY_AD_CONSENT',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
        }).create(),
        'mopa.ads.filter.segment': DataLoaderFactories['common.filter.segment'](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.ads.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.ads.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.ads.filter.activity_app_version_group_minor': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.ads.filter.activity_app_version_group_minor',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery(
                'ACTIVITY_APP_VERSION_GROUP_MINOR'
            ),
        }).create(),
        'mopa.ads.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.firebase_app_store',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
        }).create(),
        'mopa.ads.filter.firebase_country_european_union': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.sales.filter.firebase_country_european_union',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
        }).create(),
        'mopa.ads.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.ads.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.ads.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.ads.filter.attribution_traffic_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            })
        ).create(),
        'mopa.ads.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.ads.filter.firebase_install_version',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION'
                ),
            })
        ).create(),
        'mopa.ads.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.ads.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.ads.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.ads.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
        }).create(),
        'mopa.ads.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.ads.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.ads.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.ads.filter.attribution_network_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
            })
        ).create(),
        'mopa.ads.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.ads.filter.attribution_campaign_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BIVizDashboardAdEvents.sqlMap,
                filterContext: true,
            })
        ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        // 'common.modify_time'
        'mopa.ads.modify_time_ads_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.ads.modify_time_ads_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BIVizDashboardAdEvents.sqlMap.dataset,
            tableId: BIVizDashboardAdEvents.sqlMap.tableId,
        }).create(),

        // 'common.fake_datetime_data'
        'mopa.ads.fake_datetime_data': DataLoaderFactories[
            'common.fake_datetime_data'
        ]({
            createdInstanceId: 'mopa.ads.fake_datetime_data',
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.ads.revenue_platform': new AdsRevenuePlatformDataLoaderFactory({
            createdInstanceId: 'mopa.ads.revenue_platform',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.ads.ads_activity': new AdsActivityDataLoaderFactory({
            createdInstanceId: 'mopa.ads.ads_activity',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.ads.ads_dau_deu': new AdsDauDeuDataLoaderFactory({
            createdInstanceId: 'mopa.ads.ads_dau_deu',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.ads.ads_arpu': new AdsARPUDataLoaderFactory({
            createdInstanceId: 'mopa.ads.ads_arpu',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.ads.comparison': new AdsComparisonDataLoaderFactory({
            createdInstanceId: 'mopa.ads.comparison',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        // 'mopa.ads.usersdash_activity_daily': DataLoaderFactories[
        //     'common.users.usersdash_activity_daily'
        // ]({
        //     createdInstanceId: 'mopa.ads.usersdash_activity_daily',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        // }).create(),

        // 'mopa.ads.usersdash_activity_monthly': DataLoaderFactories[
        //     'common.users.usersdash_activity_monthly'
        // ]({
        //     createdInstanceId: 'mopa.ads.usersdash_activity_monthly',
        //     gcpProjectId: this.defaultGcpProjectId,
        //     queryCacheService: this.queryCache,
        // }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    DASHBOARD ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    SETTINGS ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['usd', 'pln'],
        },
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHARTS ITEMS
    ////
    /////////////////////////////////////////////////////////////////
    modify_time_ads_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.modify_time_ads_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.ads.modify_time_ads_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.modify_time_ads_datasource']
                .emitter,
    };

    // ads_revenue_user_level
    ads_revenue_user_level: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_revenue_user_level',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_revenue_user_level',
            dashboardItem: {
                title: 'Ads revenue',
                chartDocumentation: MopaAdsChartDocs.ads_revenue_user_level,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `revenue_${context.settings.currency}`;
            },
            metricName: 'Ads revenue',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };
    // ads_revenue_platform_report
    ads_revenue_platform_report: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_revenue_platform_report',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_revenue_platform_report',
            dashboardItem: {
                title: 'Ads revenue (platform report)',
                chartDocumentation:
                    MopaAdsChartDocs.ads_revenue_platform_report,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `revenue_${context.settings.currency}`;
            },
            metricName: 'Ads revenue (platform)',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.revenue_platform'].emitter,
    };

    ads_running_arpu: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_running_arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_running_arpu',
            dashboardItem: {
                title: 'Ads running ARPU',
                subtitle:
                    'Ignored filters: Activity Date, Activity App Version.',
                chartDocumentation: MopaAdsChartDocs.ads_running_arpu,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? '' : 'with_tax_';
                return `ARPU_ad_${netGross}${context.settings.currency}`;
            },
            metricName: (context) => {
                const netGross =
                    context.settings.netGross == 'net' ? '' : ' gross';
                return `Running ARPU${netGross}`;
            },
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns
                const splitColumns = context.datasourceColumns.filter(
                    (dc) => dc.state.isSplitApplied
                );
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(
                    splitColumns.map((dc) => dc.id)
                );
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label) => `Day no: ${label}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['mopa.ads.ads_arpu'].emitter,
    };

    // ads_arpdau
    ads_arpdau: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_arpdau',
            dashboardItem: {
                title: 'Ads ARPDAU',
                chartDocumentation: MopaAdsChartDocs.ads_arpdau,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `arpdau_${context.settings.currency}`;
            },
            metricName: 'Ads ARPDAU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln',
                                3
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_dau_deu'].emitter,
    };
    // ads_arpdeu
    ads_arpdeu: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_arpdeu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_arpdeu',
            dashboardItem: {
                title: 'Ads ARPDEU',
                chartDocumentation: MopaAdsChartDocs.ads_arpdeu,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `arpdeu_${context.settings.currency}`;
            },
            metricName: 'Ads ARPDEU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln',
                                3
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_dau_deu'].emitter,
    };

    // ads_engagement_rate
    ads_engagement_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ads_engagement_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ads_engagement_rate',
            dashboardItem: {
                title: 'Ads engagement rate',
                chartDocumentation: MopaAdsChartDocs.ads_engagement_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'engagement_rate',
            metricName: 'Ads engagement rate',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_dau_deu'].emitter,
    };

    // impressions
    impressions: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.impressions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.impressions',
            dashboardItem: {
                title: 'Impressions',
                chartDocumentation: MopaAdsChartDocs.impressions,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions',
            metricName: 'Impressions',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };

    // impressions_per_deu
    impressions_per_deu: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.impressions_per_deu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.impressions_per_deu',
            dashboardItem: {
                title: 'Impressions per DEU',
                chartDocumentation: MopaAdsChartDocs.impressions_per_deu,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions_per_deu',
            metricName: 'Impressions per DEU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_dau_deu'].emitter,
    };

    // completion_rate
    completion_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.completion_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.completion_rate',
            dashboardItem: {
                title: 'Completion rate',
                chartDocumentation: MopaAdsChartDocs.completion_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'completion_rate',
            metricName: 'Completion rate',
            echartsSeriesType: 'line',
            xAxisType: 'time',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns
                const splitColumns = context.datasourceColumns.filter(
                    (dc) => dc.state.isSplitApplied
                );
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(
                    splitColumns.map((dc) => dc.id)
                );
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };

    // app_fill_rate
    app_fill_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.app_fill_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.app_fill_rate',
            dashboardItem: {
                title: 'App fill rate',
                chartDocumentation: MopaAdsChartDocs.app_fill_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'app_fill_rate',
            metricName: 'App fill rate',
            echartsSeriesType: 'line',
            xAxisType: 'time',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns
                const splitColumns = context.datasourceColumns.filter(
                    (dc) => dc.state.isSplitApplied
                );
                // create layer from splitColumns
                const layer = ChartColorService.getLayerString(
                    splitColumns.map((dc) => dc.id)
                );
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };

    // errors_per_ad_watch_click
    errors_per_ad_watch_click: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.errors_per_ad_watch_click',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.errors_per_ad_watch_click',
            dashboardItem: {
                title: 'Errors per ad watch click',
                chartDocumentation:
                    MopaAdsChartDocs.errors_per_ad_watch_click,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'errors_per_ad_watch_click',
            metricName: 'Errors per ad watch click',
            echartsSeriesType: 'line',
            xAxisType: 'time',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns
                const splitColumns = context.datasourceColumns.filter(
                    (dc) => dc.state.isSplitApplied
                );
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(
                    splitColumns.map((dc) => dc.id)
                );
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts
            ) => {
                // if (!currentOptions.yAxis) currentOptions.yAxis = {};
                echartsOption.yAxis = {
                    // ...currentOptions.yAxis,
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(1),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };

    // ecpm
    ecpm: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.ecpm',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.ecpm',
            dashboardItem: {
                title: 'eCPM',
                chartDocumentation: MopaAdsChartDocs.ecpm,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `ecpm_${context.settings.currency}`;
            },
            metricName: 'eCPM',
            echartsSeriesType: 'line',
            xAxisType: 'time',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns
                const splitColumns = context.datasourceColumns.filter(
                    (dc) => dc.state.isSplitApplied
                );
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(
                    splitColumns.map((dc) => dc.id)
                );
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.ads.ads_activity'].emitter,
    };

    comparison_impressions: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.comparison_impressions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.comparison_impressions',
            dashboardItem: {
                title: 'Impressions comparison',
                subtitle:
                    'Absolute difference calculated as am_bi - platform_report',
                chartDocumentation: MopaAdsChartDocs.comparison_impressions,
            },
            xColumnLabel: 'date',
            yColumnLabel: (_: IQueryContext) => {
                return `impressions_cnt_abs_diff`;
            },
            metricName: 'Impressions absolute difference',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['mopa.ads.comparison'].emitter,
    };
    comparison_revenue: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.comparison_revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.comparison_revenue',
            dashboardItem: {
                title: 'Revenue comparison',
                subtitle:
                    'Absolute difference calculated as am_bi - platform_report',
                chartDocumentation: MopaAdsChartDocs.comparison_revenue,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                return `revenue_${context.settings.currency}_abs_diff`;
            },
            metricName: 'Revenue absolute difference',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    // color: this.colorService.getColor(layer, s),
                    // it is safe to use empty layer
                    color: this.colorService.getColor('', s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['mopa.ads.comparison'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
        > = {
        AD_PLATFORM: {
            id: 'mopa.ads.filter.ad_platform',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.ad_platform',
                    'AD_PLATFORM'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.ad_platform'].emitter,
        },
        AD_NAME: {
            id: 'mopa.ads.filter.ad_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.ad_name',
                    'AD_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.ad_name'].emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.ads.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.ads.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.ads.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.ads.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.activity_app_version']
                    .emitter,
        },
        DAILY_AD_CONSENT: {
            id: 'mopa.ads.filter.daily_ad_consent',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.daily_ad_consent',
                    'DAILY_AD_CONSENT'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.daily_ad_consent']
                    .emitter,
        },
        SEGMENT: {
            id: 'mopa.ads.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.ads.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.segment'].emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.ads.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.ads.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.ads.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.firebase_app_store']
                    .emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.ads.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.ads.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.firebase_country_tier']
                    .emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.ads.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.firebase_country']
                    .emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.ads.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.attribution_traffic_source'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.ads.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.attribution_network_name'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.ads.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.ads.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.ads.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.etl_payer'].emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.ads.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.ads.filter.etl_revenue_badge']
                    .emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.ads.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.ads.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.ads.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaUsersComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaUsersComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 90 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        return initialContext;
    }
}
